import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { ICalendarEvent } from '../Interfaces/Calendar/ICalendarEvent';
import { customFetcher } from './useFetch';

/** Event-Payload mit optionaler Calendar-Connection. Wenn `idUserCalendarConnection`
 *  gesetzt ist, leitet das Backend an den passenden Provider-Adapter (O365 oder
 *  WebDAV) — sonst Fallback auf die alte 1:1-O365-Logik. */
export type CalendarEventPayload = ICalendarEvent & {
  idUserCalendarConnection?: number | null;
};

/** Helper: Liste in der Query-Cache patchen (Append / Replace / Remove). */
const calendarEventsKey = (userId: number) => ['calendarEvents', userId];

const patchEventsList = (
  list: ICalendarEvent[] | undefined,
  mutation: 'add' | 'update' | 'remove',
  event: ICalendarEvent
): ICalendarEvent[] => {
  const base = list ?? [];
  if (mutation === 'remove') {
    return base.filter(e => e.id !== event.id);
  }
  if (mutation === 'add') {
    // Idempotenz: falls dieselbe id schon drin ist (z. B. doppeltes
    // Submit), ersetzen statt duplizieren.
    return [...base.filter(e => e.id !== event.id), event];
  }
  // update
  const i = base.findIndex(e => e.id === event.id);
  if (i < 0) return [...base, event];
  const copy = base.slice();
  copy[i] = { ...copy[i], ...event };
  return copy;
};

/**
 * Stale-While-Revalidate für Calendar-Events:
 *   - staleTime 60s: innerhalb einer Minute keine erneuten GETs
 *   - gcTime 30min: Cache bleibt nach Unmount erhalten → Wechsel
 *     auf andere Menüpunkte und zurück zeigt sofort die letzten Daten
 *   - refetchOnMount: true (Default): bei Remount nur fetchen wenn stale
 *   - refetchInterval 5min: Hintergrund-Refresh, ohne UI zu blockieren
 *   - refetchOnWindowFocus: false (User-Wunsch, kein Flackern beim
 *     Fenster-Wechsel)
 */
export const useGetCalendarEvents = (
  userId: number,
  start?: string,
  end?: string
) => {
  const {
    data,
    isFetching: isFetchingCalendarEvents,
    isError: isErrorCalendarEvents,
    refetch: refetchCalendarEvents,
  } = useQuery<ICalendarEvent[]>({
    refetchOnWindowFocus: false,
    // Range im Key: Blaettern ueber den bisher geladenen Bereich hinaus loest
    // einen Nachlade-Request aus. Das Backend zieht dabei nur die fehlende
    // Rand-Scheibe (Delta) und liefert den gemergten Gesamtbestand zurueck.
    queryKey: [...calendarEventsKey(userId), start ?? null, end ?? null],
    retry: false,
    staleTime: 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    refetchIntervalInBackground: false,
    // Beim Fenster-Wechsel die bisherigen Events sichtbar lassen, waehrend der
    // groessere Bereich im Hintergrund nachgeladen wird (kein Leer-Flackern).
    placeholderData: keepPreviousData,
    queryFn: () => {
      const params = new URLSearchParams();
      if (start) params.set('start', start);
      if (end) params.set('end', end);
      const qs = params.toString();
      return customFetcher({
        endpoint: `/calendar/events/${userId}${qs ? `?${qs}` : ''}`,
      });
    },
  });

  return {
    calendarEvents: data,
    isFetchingCalendarEvents,
    isErrorCalendarEvents,
    refetchCalendarEvents,
  };
};

export const useCreateCalendarEvent = (userId: number) => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: createCalendarEvent,
    isPending: isPendingCreateCalendarEvent,
  } = useMutation({
    mutationFn: (event: CalendarEventPayload) =>
      customFetcher({
        endpoint: `/calendar/events/${userId}`,
        method: 'POST',
        body: event,
      }) as Promise<ICalendarEvent>,
    // Sofortiges UI-Update: das vom Backend zurückgegebene Event direkt
    // in den React-Query-Cache eintragen — kein zusätzlicher GET-Roundtrip.
    // setQueriesData (Prefix-Match), damit ALLE geladenen Fenster-Varianten
    // gepatcht werden, nicht nur eine exakte Range.
    onSuccess: created => {
      queryClient.setQueriesData<ICalendarEvent[]>(
        { queryKey: calendarEventsKey(userId) },
        prev => patchEventsList(prev, 'add', created)
      );
    },
  });

  return { createCalendarEvent, isPendingCreateCalendarEvent };
};

export const useUpdateCalendarEvent = (userId: number) => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: updateCalendarEvent,
    isPending: isPendingUpdateCalendarEvent,
  } = useMutation({
    mutationFn: (event: CalendarEventPayload) =>
      customFetcher({
        endpoint: `/calendar/events/${userId}`,
        method: 'PUT',
        body: event,
      }) as Promise<ICalendarEvent>,
    onSuccess: updated => {
      queryClient.setQueriesData<ICalendarEvent[]>(
        { queryKey: calendarEventsKey(userId) },
        prev => patchEventsList(prev, 'update', updated)
      );
    },
  });

  return { updateCalendarEvent, isPendingUpdateCalendarEvent };
};

/**
 * Force-Refresh: ruft das Backend auf, um den serverseitigen Event-Cache
 * fuer alle aktiven Kalender des Users neu zu fuellen, und invalidiert
 * danach die Events-Query, damit das Frontend sofort die frischen Daten
 * sieht.
 */
export const useRefreshCalendarCache = (userId: number) => {
  const queryClient = useQueryClient();
  const { mutateAsync: refreshCalendarCache, isPending: isRefreshing } =
    useMutation({
      mutationFn: () =>
        customFetcher({
          endpoint: `/calendar/refresh/${userId}`,
          method: 'POST',
          body: {},
        }) as Promise<{
          refreshed: number;
          failed: number;
          details: Array<{
            idUserCalendarConnection: number;
            Label?: string;
            ok: boolean;
            message: string;
          }>;
        }>,
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: calendarEventsKey(userId),
        });
      },
    });
  return { refreshCalendarCache, isRefreshing };
};

export const useDeleteCalendarEvent = (userId: number) => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: deleteCalendarEvent,
    isPending: isPendingDeleteCalendarEvent,
  } = useMutation({
    // Optimistic-Update: das Event verschwindet sofort aus der Liste,
    // bevor das Backend antwortet. Schlägt das Löschen fehl, rollt
    // ``onError`` den Cache zurück.
    mutationFn: (event: CalendarEventPayload) =>
      customFetcher({
        endpoint: `/calendar/events/${userId}`,
        method: 'DELETE',
        body: event,
      }),
    onMutate: event => {
      const filters = { queryKey: calendarEventsKey(userId) };
      // Snapshot ALLER Fenster-Varianten fuer ein sauberes Rollback.
      const previous = queryClient.getQueriesData<ICalendarEvent[]>(filters);
      queryClient.setQueriesData<ICalendarEvent[]>(filters, prev =>
        patchEventsList(prev, 'remove', event)
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      context?.previous?.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
    },
  });

  return {
    deleteCalendarEvent,
    isPendingDeleteCalendarEvent,
  };
};
